import type { Dto } from '@lmc/ts-common/lib/utils/errors'
import _ from 'lodash'
import CONSTANTS from 'utils/constants'
import store from '../../store'
import { getHomeChangedModalOpen } from './homeSwitchBroadcast'
import {
  checkMFAForce,
  HomeChangedError,
  homeChangedMiddleware,
  ssoMiddleware
} from './sso'

let requestAbortController: AbortController

type RequestMethod = 'GET' | 'POST' | 'DELETE' | 'PUT' | 'PATCH'
type RequestContentType = 'application/json' | 'multipart/form-data'
type RequestResponseType = 'json' | 'text' | 'arrayBuffer'
type LmcRequestParams = Parameters<typeof lmcRequest>[0]

function getBaseUrl(url: string, isRota?: boolean): string {
  if (url.startsWith('/emar') || url.startsWith('emar')) {
    if (
      typeof window !== 'undefined' &&
      window.location.hostname === 'localhost'
    ) {
      return ''
    }
    return CONSTANTS.VITE_APP_EMAR_API || ''
  }
  if (isRota) return CONSTANTS.VITE_APP_ROTA_API
  // Strip the version prefix from the base URL when the request
  // path already includes one, to avoid duplication (e.g. /v5/v6/...)
  let cmApi = CONSTANTS.VITE_APP_CM_API
  if (cmApi && url.match(/^\/?(v\d+)(?:\/|$)/i)) {
    cmApi = cmApi.replace(/\/v\d+(?=\/?$)/i, '')
  }
  return cmApi
}

async function lmcRequest(params: {
  body?: any
  contentType?: RequestContentType
  method: RequestMethod
  newSignal?: boolean
  responseType?: RequestResponseType
  url: string
  isRota?: boolean
  headers?: Record<string, string>
  cache?: RequestCache
}) {
  if (params.newSignal === undefined) {
    params.newSignal = false
  }

  const baseUrl = getBaseUrl(params.url, params.isRota)
  let urlPath = params.url
  if (baseUrl && (urlPath.startsWith('/emar') || urlPath.startsWith('emar'))) {
    urlPath = urlPath.replace(/^\/?emar/, '')
  }
  const fullUrl = baseUrl
    ? `${baseUrl}${urlPath.startsWith('/') ? '' : '/'}${urlPath}`
    : urlPath

  const headers: HeadersInit = {
    Accept: 'application/json'
  }

  const state = store.getState() as any
  const homeId = state.global.home?.id
  if (homeId) {
    headers['X-Home-Id'] = homeId
  }

  if (params.contentType) {
    headers['Content-Type'] = params.contentType
  }
  if (params.headers) {
    Object.assign(headers, params.headers)
  }

  if (params.newSignal || !requestAbortController) {
    if (requestAbortController) {
      requestAbortController.abort()
    }
    requestAbortController = new AbortController()
  }
  const requestOptions: RequestInit = {
    credentials: 'include',
    method: params.method,
    headers,
    signal: requestAbortController.signal,
    // Opt-in cache mode. JSON API reads that revalidate to 304 break getJSON
    // (304 is not `response.ok`), so callers can pass 'no-store' to always get
    // a fresh 200 body; React Query handles in-memory caching anyway.
    ...(params.cache ? { cache: params.cache } : {})
  }
  if (params.body) {
    requestOptions.body = params.body
  }

  if (getHomeChangedModalOpen()) {
    return { response: new Response(null, { status: 409 }), result: {} }
  }

  try {
    const request = new Request(fullUrl, requestOptions)
    const response = await fetch(request)
    await ssoMiddleware(response)
    await homeChangedMiddleware(response)

    // handle response body
    let result: ArrayBuffer | string | Record<string, any>

    if (params.responseType === 'arrayBuffer') {
      result = await response.clone().arrayBuffer()
    } else if (params.responseType === 'json') {
      result = await response.clone().json()

      // } else if (params.responseType === 'text') {
    } else {
      result = await response.clone().text()
    }

    return {
      response,
      result
    }
  } catch (_e: any) {
    if (_e instanceof HomeChangedError) {
      return { response: new Response(null, { status: 409 }), result: {} }
    }
    const e: Error = _e
    throw e
  }
}

export const getJSON = async (params: {
  match?: boolean
  newSignal?: boolean
  responseType?: RequestResponseType
  url: string
  isRota?: boolean
  customErrorHandling?: boolean
  headers?: Record<string, string>
  cache?: RequestCache
}) => {
  if (params.responseType === undefined) {
    params.responseType = 'json'
  }
  if (params.match === undefined) {
    params.match = true
  }

  if (params.match ? !params.url.match(/\?/) : false) {
    if (params.url.match(/residents$/)) {
      params.url += '?anyStatus=true'
    }
  }

  const params2: LmcRequestParams = {
    contentType: 'application/json',
    method: 'GET',
    newSignal: params.newSignal,
    responseType: params.responseType,
    url: params.url,
    isRota: params?.isRota,
    headers: params.headers,
    cache: params.cache
  }

  try {
    const request = await lmcRequest(params2)
    const { response } = request
    const result = request.result as Record<string, any>

    checkMFAForce(result as Dto)

    if (!params?.customErrorHandling && !response.ok) {
      throw new Error(
        result.error || `Request failed with status ${response.status}`
      )
    }

    return result as any
  } catch (_e: any) {
    const e: Error = _e
    console.log(e)
    throw e
  }
}

export const get = async (params: {
  newSignal?: boolean
  responseType?: RequestResponseType
  url: string
}) => {
  if (params.responseType === undefined) {
    params.responseType = 'json'
  }

  const params2: LmcRequestParams = {
    contentType: 'application/json',
    method: 'GET',
    newSignal: params.newSignal,
    responseType: params.responseType,
    url: params.url
  }

  const request = await lmcRequest(params2)
  const { response } = request
  const result = request.result as Record<string, any>

  checkMFAForce(result as Dto)

  if (!response.ok) {
    throw response
  }

  return result as any
}

export const postJSON = async (params: {
  body?: any
  contentType?: RequestContentType
  json?: any
  method?: RequestMethod
  newSignal?: boolean
  responseType?: RequestResponseType
  url: string
  isRota?: boolean
}) => {
  if (params.method === undefined) {
    params.method = 'POST'
  }
  if (params.contentType === undefined) {
    params.contentType = 'application/json'
  }
  if (params.contentType === 'multipart/form-data') {
    delete params.contentType
  }
  if (!params.responseType) {
    params.responseType = 'json'
  }
  const params2: LmcRequestParams = {
    body:
      params.body ?? (params.json ? JSON.stringify(params.json) : undefined),
    contentType: params.contentType,
    method: params.method,
    newSignal: params.newSignal,
    responseType: params.responseType,
    url: params.url,
    isRota: params?.isRota
  }

  const request = await lmcRequest(params2)

  const { response } = request
  const result = request.result as any

  checkMFAForce(result)

  if (!response.ok) {
    let serverError: string
    if (result.message || result.msg) {
      throw result
    }
    if (response.status === 403) {
      serverError = "Oops! You don't have access to this feature."
    } else if (response.status === 400) {
      serverError = handleValidationError(result)
    } else {
      serverError =
        'Oops! Something went wrong. Please refresh the page and try again.'
    }

    throw new Error(serverError)
  }

  return result
}

export const setFirstLogin = () =>
  new Promise((resolve, reject) => {
    postJSON({
      url: `/api/reports/user/firstlogin`
    })
      .catch((err) => {
        console.log(err)
        // eslint-disable-next-line prefer-promise-reject-errors
        reject({
          message: 'Could not set first login.'
        })
      })
      .then((result) => {
        resolve(result)
        console.log(result)
      })
  })

export const deleteItem = async (id: string, list: string) => {
  const url = `/api/list/${list}/${id}`
  return new Promise((resolve, reject) => {
    const params: LmcRequestParams = {
      method: 'DELETE',
      url
    }
    lmcRequest(params)
      .then((request) => resolve(request.response))
      .catch((e) => {
        console.log(e)
        // eslint-disable-next-line prefer-promise-reject-errors
        reject({
          message: 'The item could not be deleted at this time.'
        })
      })
  })
}

function handleValidationError(error: any) {
  if (error?.detail && error?.detail?.password) {
    return _.capitalize(error.detail.password.error)
  }
  if (error?.error?.userExists) {
    return error.error
  }
  if (typeof error?.error === 'string') {
    return error.error
  }
  if (typeof error?.message === 'string') {
    return error.message
  }
  return 'Oops! There were validation errors. Please check all information is correct and try again.'
}
